// src/app/components/navigation/site-header.tsx
//
// The single top header for every "shell-less" page — the Upmode landing page
// (upmode-home) and the admin sections (Global Settings, User Management,
// Associate Brands, Brand API Keys, Onboarding). It used to exist twice: as an
// inline block inside upmode-home.tsx and as the visually different
// top-nav-bar.tsx on the admin pages, so the admin areas never looked like the
// product's front door. This component IS the landing-page header, extracted,
// plus a Home entry in the admin Tools menu so there is a way back from an
// admin section. The bar itself stays as-is — the wordmark is the other way
// home.

import { useEffect, useRef, useState, type RefObject, type MouseEvent as ReactMouseEvent } from "react";
import { BookMarked, ChevronDown, FlaskConical, Home, Library, LogOut, Rocket, SlidersHorizontal } from "lucide-react";

/** The page key the Upmode wordmark / Tools ▸ Home navigates to. */
export const HOME_PAGE = "brand-search";

interface SiteHeaderProps {
  /**
   * Current app page key. Drives which Tools item is marked active and
   * whether "How Upmode works" scrolls in-page or navigates home first.
   */
  currentPage?: string;
  onNavigate?: (page: string) => void;
  onLogout?: () => void;
}

function getStoredUser() {
  const fullName = localStorage.getItem("full_name") || sessionStorage.getItem("full_name") || "";
  const username = localStorage.getItem("username") || sessionStorage.getItem("username") || "";
  const displayName = fullName.trim() || username.split("@")[0] || "User";
  return { displayName, username };
}

function getIsAdmin() {
  return localStorage.getItem("is_admin") === "true" || sessionStorage.getItem("is_admin") === "true";
}

/** Close `open` on an outside click or Escape. */
function useDismiss(open: boolean, ref: RefObject<HTMLElement | null>, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open, ref, close]);
}

const LINK_STYLE = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--muted-foreground)",
  textDecoration: "none",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: "6px 8px",
  borderRadius: 8,
  fontFamily: "inherit",
} as const;

export function SiteHeader({ currentPage, onNavigate, onLogout }: SiteHeaderProps) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [userOpen, setUserOpen]   = useState(false);
  const toolsRef = useRef<HTMLDivElement | null>(null);
  const userRef  = useRef<HTMLDivElement | null>(null);

  const user = getStoredUser();
  const isAdmin = getIsAdmin();
  const isHome = currentPage === undefined || currentPage === HOME_PAGE;

  useDismiss(toolsOpen, toolsRef, () => setToolsOpen(false));
  useDismiss(userOpen,  userRef,  () => setUserOpen(false));

  const goHome = () => onNavigate?.(HOME_PAGE);

  // "How Upmode works" is a section of the home page. Off home there is
  // nothing to scroll to, so navigate home first and then scroll to it.
  const goHowItWorks = () => {
    if (isHome) {
      document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    goHome();
    setTimeout(() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" }), 150);
  };

  const toolsItems = [
    { key: HOME_PAGE,         label: "Home",               Icon: Home },
    { key: "onboarding",      label: "Onboard a customer", Icon: Rocket },
    // Pre-deployment content test (control vs injection, judged and gated).
    { key: "wind-tunnel",     label: "Wind tunnel",        Icon: FlaskConical },
    // Everything we know about a client, append-only, with its sources (#131).
    { key: "brand-record",    label: "Brand record",       Icon: BookMarked },
    // Every approved framework, anonymised, with how it measured (#139).
    { key: "framework-library", label: "Framework library", Icon: Library },
    // User Management, Associate Brands & Brand API Keys are tabs inside
    // Global Settings, alongside the Platform switches.
    { key: "global-settings", label: "Global Settings",    Icon: SlidersHorizontal },
  ];

  const hoverIn = (e: ReactMouseEvent<HTMLElement>) => {
    e.currentTarget.style.background = "var(--alpha-teal-10)";
    e.currentTarget.style.color = "var(--alpha-teal)";
  };
  const hoverOut = (color: string) => (e: ReactMouseEvent<HTMLElement>) => {
    e.currentTarget.style.background = "transparent";
    e.currentTarget.style.color = color;
  };

  return (
    <div
      style={{
        position: "sticky", top: 0, zIndex: 30,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: 64, padding: "0 28px",
        background: "rgba(255,255,255,0.82)",
        backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      {/* Keyframes for the dropdowns. upmode-home defines `upFade` for its own
          sections; the admin pages that mount this header do not, so it is
          declared here to keep the header self-contained. */}
      <style>{`@keyframes upFade{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}`}</style>

      <button
        onClick={goHome}
        title="Home"
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 22, fontWeight: 800, letterSpacing: "-0.022em", color: "var(--graphite)", fontFamily: "inherit" }}
      >
        Upmode
      </button>

      <div className="flex items-center" style={{ gap: 16 }}>
        <button
          onClick={goHowItWorks}
          className="hidden sm:inline-flex items-center"
          style={LINK_STYLE}
          onMouseEnter={hoverIn}
          onMouseLeave={hoverOut("var(--muted-foreground)")}
        >
          How Upmode works
        </button>

        {isAdmin && (
          <div className="relative" ref={toolsRef}>
            <button
              onClick={() => setToolsOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={toolsOpen}
              className="inline-flex items-center"
              style={{ gap: 4, padding: "6px 8px", borderRadius: 8, background: toolsOpen ? "var(--muted)" : "transparent", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, fontFamily: "inherit", color: toolsOpen ? "var(--foreground)" : "var(--muted-foreground)" }}
            >
              Tools
              <ChevronDown style={{ width: 13, height: 13, transition: "transform 180ms var(--ease-standard)", transform: toolsOpen ? "rotate(180deg)" : "none" }} />
            </button>
            {toolsOpen && (
              <div
                role="menu"
                style={{ position: "absolute", right: 0, top: "calc(100% + 10px)", zIndex: 41, minWidth: 248, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--elevation-lg)", overflow: "hidden", animation: "upFade 150ms var(--ease-standard)" }}
              >
                <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", color: "var(--muted-foreground)" }}>ADMIN</span>
                </div>
                {toolsItems.map((it, i) => {
                  const active = it.key === currentPage;
                  return (
                    <button
                      key={it.key}
                      role="menuitem"
                      onClick={() => { setToolsOpen(false); onNavigate?.(it.key); }}
                      className="w-full inline-flex items-center"
                      style={{ gap: 10, padding: "11px 16px", background: active ? "var(--alpha-teal-10)" : "transparent", border: "none", borderTop: i === 0 ? "none" : "1px solid var(--border)", cursor: "pointer", color: active ? "var(--alpha-teal)" : "var(--foreground)", textAlign: "left", fontFamily: "inherit" }}
                      onMouseEnter={hoverIn}
                      onMouseLeave={hoverOut(active ? "var(--alpha-teal)" : "var(--foreground)")}
                    >
                      <it.Icon style={{ width: 16, height: 16, flexShrink: 0 }} />
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{it.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <span style={{ width: 1, height: 22, background: "var(--border)" }} />

        <div className="relative" ref={userRef}>
          <button
            onClick={() => setUserOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={userOpen}
            className="inline-flex items-center"
            style={{ gap: 9, background: "none", border: "none", cursor: "pointer", padding: 0 }}
            title="Account"
          >
            <span style={{ width: 30, height: 30, borderRadius: 9999, background: "var(--graphite)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600 }}>
              {user.displayName.charAt(0).toUpperCase()}
            </span>
            <span className="hidden sm:inline" style={{ fontSize: 14, fontWeight: 500, color: "var(--foreground)" }}>
              {user.displayName}
            </span>
          </button>

          {userOpen && (
            <div
              role="menu"
              style={{ position: "absolute", right: 0, top: "calc(100% + 10px)", zIndex: 41, minWidth: 232, maxWidth: "min(280px, calc(100dvw - 32px))", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--elevation-lg)", overflow: "hidden", animation: "upFade 150ms var(--ease-standard)" }}
            >
              <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", minWidth: 0 }}>
                <div className="truncate" style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)" }}>{user.displayName}</div>
                {user.username && (
                  <div className="truncate" style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 2 }}>{user.username}</div>
                )}
              </div>
              <button
                role="menuitem"
                onClick={() => { setUserOpen(false); onLogout?.(); }}
                className="w-full inline-flex items-center"
                style={{ gap: 10, padding: "11px 16px", background: "transparent", border: "none", cursor: "pointer", color: "var(--muted-foreground)", textAlign: "left", fontFamily: "inherit" }}
                onMouseEnter={hoverIn}
                onMouseLeave={hoverOut("var(--muted-foreground)")}
              >
                <LogOut style={{ width: 16, height: 16, flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 500 }}>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
